import { useState } from "react";
import { SettingsPage } from "@/components/v2/settings-page";
import { SettingsField } from "@/components/v2/settings-field";
import { FormSheetFooter } from "@/components/v2/form-sheet-footer";
import { EmptyState } from "@/components/v2/empty-state";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useInviteBusinessMember } from "@/hooks/use-business-members";

const BUSINESS_ROLES = [
  { value: "admin", label: "Admin" },
  { value: "supervisor", label: "Supervisor" },
  { value: "member", label: "Member" },
  { value: "worker", label: "Worker" },
  { value: "viewer", label: "Viewer" },
] as const;

function InviteTeamMemberSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    message: string;
    action: "invited_new" | "added_existing" | "already_member";
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const invite = useInviteBusinessMember();

  function handleClose() {
    setEmail("");
    setRole("member");
    setResult(null);
    setError(null);
    onClose();
  }

  async function handleSubmit() {
    setError(null);
    setResult(null);
    setSubmitting(true);
    try {
      const data = await invite.mutateAsync({
        email,
        role: role as (typeof BUSINESS_ROLES)[number]["value"],
      });
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send invite");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen: boolean) => !isOpen && handleClose()}
    >
      <SheetContent className="w-[400px] sm:max-w-[400px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Invite Team Member</SheetTitle>
        </SheetHeader>

        {result ? (
          <div className="flex-1 py-4 space-y-4">
            <div
              className={`rounded-md p-3 typo-body ${
                result.action === "already_member"
                  ? "bg-[color:var(--color-surface)] text-[color:var(--color-text-md)]"
                  : "bg-[color:var(--color-success)]/10 text-[color:var(--color-success)]"
              }`}
            >
              {result.message}
            </div>
          </div>
        ) : (
          <form
            id="invite-form"
            className="flex-1 py-4 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <SettingsField label="Email">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </SettingsField>
            <SettingsField label="Role">
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SettingsField>
            {error && <p className="typo-body text-destructive">{error}</p>}
          </form>
        )}

        {result ? (
          <FormSheetFooter onCancel={handleClose} submitLabel="Done" />
        ) : (
          <FormSheetFooter
            onCancel={handleClose}
            submitLabel="Send Invite"
            isSubmitting={submitting || !email.trim()}
            formId="invite-form"
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function V2SettingsTeamPage() {
  const [inviteOpen, setInviteOpen] = useState(false);

  return (
    <SettingsPage title="Team Members">
      <EmptyState
        title="No team members"
        description="Invite team members to start collaborating."
        action={{
          label: "Invite Member",
          onClick: () => setInviteOpen(true),
        }}
      />
      <InviteTeamMemberSheet
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />
    </SettingsPage>
  );
}
